import { defineStore } from 'pinia'
import { usePinnedSharesStore } from 'src/CrudManager/Core/stores/usePinnedSharesStore'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { computed, ref } from 'vue'
import { useNotificationApi } from '../composables/useNotificationApi'
import type {
  EchoGroupActionEvent,
  EchoNotificationEvent,
  EchoProgressUpdateEvent,
  Notification,
  NotificationFilters,
  NotificationPriority,
  NotificationUpdatePayload,
  PaginatedNotifications,
  ProgressData,
} from '../types'

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref<Notification[]>([])
  const unreadCount = ref(0)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const realTimeEnabled = ref(false)
  const lastSync = ref<string | null>(null)
  const reactivityTrigger = ref(0) // Used to force reactivity updates
  const currentPage = ref(1)
  const totalPages = ref(1)
  const perPage = ref(20)
  const total = ref(0)
  const initialized = ref(false)

  const filters = ref<NotificationFilters>({
    read: null,
    priority: null,
    type: null,
    progressive: null,
    mandatory: null,
  })

  // Getters
  const unreadNotifications = computed(() => {
    // Include reactivity trigger to ensure updates when notifications change via real-time
    reactivityTrigger.value // eslint-disable-line @typescript-eslint/no-unused-expressions
    return notifications.value.filter((n) => !n.read_at)
  })

  const mandatoryNotifications = computed(() => {
    // Include reactivity trigger to ensure updates when notifications change via real-time
    reactivityTrigger.value // eslint-disable-line @typescript-eslint/no-unused-expressions
    return notifications.value.filter((n) => n.mandatory && !n.read_at)
  })

  const progressiveNotifications = computed(() => {
    // Include reactivity trigger to ensure updates when notifications change via real-time
    reactivityTrigger.value // eslint-disable-line @typescript-eslint/no-unused-expressions
    return notifications.value.filter((n) => n.is_progressive)
  })

  const filteredNotifications = computed(() => {
    let filtered = [...notifications.value]

    if (filters.value.read === true) {
      filtered = filtered.filter((n) => n.read_at)
    } else if (filters.value.read === false) {
      filtered = filtered.filter((n) => !n.read_at)
    }

    if (filters.value.priority) {
      filtered = filtered.filter((n) => n.priority === filters.value.priority)
    }

    if (filters.value.type) {
      filtered = filtered.filter((n) => n.type.includes(filters.value.type!))
    }

    if (filters.value.progressive !== null) {
      filtered = filtered.filter((n) => n.is_progressive === filters.value.progressive)
    }

    if (filters.value.mandatory !== null) {
      filtered = filtered.filter((n) => n.mandatory === filters.value.mandatory)
    }

    return filtered.sort((a, b) => {
      // Sort by priority first
      const priorityOrder: Record<NotificationPriority, number> = {
        urgent: 4,
        high: 3,
        normal: 2,
        low: 1,
      }

      const aPriority = priorityOrder[a.priority || 'normal']
      const bPriority = priorityOrder[b.priority || 'normal']

      if (aPriority !== bPriority) {
        return bPriority - aPriority
      }

      // Then by read status (unread first)
      if (!a.read_at && b.read_at) return -1
      if (a.read_at && !b.read_at) return 1

      // Finally by creation date (newest first)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  })

  const getNotificationById = computed(() => {
    return (id: string) => notifications.value.find((n) => n.id === id)
  })

  const hasUnreadMandatory = computed(() => mandatoryNotifications.value.length > 0)

  // Actions
  const setNotifications = (newNotifications: Notification[]) => {
    notifications.value = newNotifications
    updateUnreadCount()
  }

  const addNotification = (notification: Notification) => {
    const existingIndex = notifications.value.findIndex((n) => n.id === notification.id)
    if (existingIndex >= 0) {
      notifications.value[existingIndex] = notification
    } else {
      notifications.value.unshift(notification)
    }
    updateUnreadCount()

    // Trigger reactivity update for computed properties
    reactivityTrigger.value++
  }

  const updateNotification = (notification: Notification) => {
    const index = notifications.value.findIndex((n) => n.id === notification.id)
    if (index >= 0) {
      notifications.value[index] = { ...notifications.value[index], ...notification }
      updateUnreadCount()

      // Trigger reactivity update for computed properties
      reactivityTrigger.value++
    }
  }

  const updateNotificationProgress = (payload: NotificationUpdatePayload) => {
    const notification = notifications.value.find((n) => n.id === payload.notification_id)
    if (notification) {
      if (payload.progress_status) {
        notification.progress_status = payload.progress_status
      }
      if (payload.progress_data) {
        notification.progress_data = {
          ...notification.progress_data,
          ...payload.progress_data,
        } as ProgressData
      }
      if (payload.completed_at) {
        notification.completed_at = payload.completed_at
      }

      // Trigger reactivity update for computed properties
      reactivityTrigger.value++
    }
  }

  const markAsRead = (notificationId: string) => {
    const notification = notifications.value.find((n) => n.id === notificationId)
    if (notification && !notification.read_at) {
      notification.read_at = new Date().toISOString()
      updateUnreadCount()
    }
  }

  const markAsUnread = (notificationId: string) => {
    const notification = notifications.value.find((n) => n.id === notificationId)
    if (notification && notification.read_at) {
      notification.read_at = null
      updateUnreadCount()
    }
  }

  const markAllAsRead = () => {
    const now = new Date().toISOString()
    notifications.value.forEach((n) => {
      if (!n.read_at) {
        n.read_at = now
      }
    })
    updateUnreadCount()
  }

  const removeNotification = (notificationId: string) => {
    const index = notifications.value.findIndex((n) => n.id === notificationId)
    if (index >= 0) {
      notifications.value.splice(index, 1)
      updateUnreadCount()
    }
  }

  const updateUnreadCount = () => {
    unreadCount.value = notifications.value.filter((n) => !n.read_at).length
  }

  const setLoading = (isLoading: boolean) => {
    loading.value = isLoading
  }

  const setError = (errorMessage: string | null) => {
    error.value = errorMessage
  }

  const setFilters = (newFilters: Partial<NotificationFilters>) => {
    filters.value = { ...filters.value, ...newFilters }
  }

  const clearFilters = () => {
    filters.value = {
      read: null,
      priority: null,
      type: null,
      progressive: null,
      mandatory: null,
    }
  }

  const setPagination = (page: number, lastPage: number, perPageCount: number, totalCount: number) => {
    currentPage.value = page
    totalPages.value = lastPage
    perPage.value = perPageCount
    total.value = totalCount
  }

  const setRealTimeEnabled = (enabled: boolean) => {
    realTimeEnabled.value = enabled
  }

  const setLastSync = () => {
    lastSync.value = new Date().toISOString()
  }

  const autoPinIfShare = (notification: Notification) => {
    const data = notification.data as Record<string, unknown> | undefined
    if (data?.type !== 'crud_model_shared') return
    const pinned = usePinnedSharesStore()
    pinned.pinFromNotification({
      id: notification.id,
      created_at: notification.created_at ?? null,
      data,
    })
  }

  // Real-time event handlers
  const handleEchoNotificationEvent = (event: EchoNotificationEvent) => {
    // Parse the event type from the channel
    const eventType = event.channel?.split('.').pop() || 'created'

    switch (eventType) {
      case 'created':
        addNotification(event.notification)
        autoPinIfShare(event.notification)
        break
      case 'updated':
        updateNotification(event.notification)
        break
      case 'read':
        markAsRead(event.notification.id)
        break
      case 'deleted':
        removeNotification(event.notification.id)
        break
    }
  }

  const handleEchoProgressUpdate = (event: EchoProgressUpdateEvent) => {
    console.log('Echo progress update received:', event)

    updateNotificationProgress({
      notification_id: event.notification_id,
      progress_data: event.progress_data,
      progress_status: event.progress_status,
      completed_at: event.completed_at,
    })
  }

  const handleEchoGroupAction = (event: EchoGroupActionEvent) => {
    console.log('Echo group action received:', event)

    // Update all notifications in the group
    notifications.value.forEach((notification) => {
      if (notification.group_id === event.group_id) {
        notification.completed_at = event.completed_at
        // Remove actions that are no longer available
        if (notification.actions) {
          Object.keys(notification.actions).forEach((actionName) => {
            if (notification.actions![actionName].group_action) {
              delete notification.actions![actionName]
            }
          })
        }
      }
    })

    // Trigger reactivity update for computed properties
    reactivityTrigger.value++
  }

  const initialize = async () => {
    if (initialized.value) return

    const userStore = useUserStore()
    if (!userStore.user?.id) {
      console.log('Cannot initialize notifications: No user available')
      return
    }

    try {
      console.log('Initializing notification store...')
      const api = useNotificationApi()

      // Load initial notifications
      loading.value = true
      error.value = null

      const result: PaginatedNotifications = await api.fetchNotifications(1, 20)
      setNotifications(result.data)
      setPagination(result.current_page, result.last_page, result.per_page, result.total)
      setLastSync()

      initialized.value = true
      console.log(`Notification store initialized with ${result.data.length} notifications`)
    } catch (err) {
      console.error('Failed to initialize notification store:', err)
      error.value = 'Failed to load notifications'
    } finally {
      loading.value = false
    }
  }

  const clear = () => {
    notifications.value = []
    unreadCount.value = 0
    loading.value = false
    error.value = null
    realTimeEnabled.value = false
    lastSync.value = null
    currentPage.value = 1
    totalPages.value = 1
    perPage.value = 20
    total.value = 0
    initialized.value = false
    clearFilters()
  }

  return {
    // State
    notifications,
    unreadCount,
    loading,
    error,
    filters,
    realTimeEnabled,
    lastSync,
    currentPage,
    totalPages,
    perPage,
    total,
    initialized,

    // Getters
    unreadNotifications,
    mandatoryNotifications,
    progressiveNotifications,
    filteredNotifications,
    getNotificationById,
    hasUnreadMandatory,

    // Actions
    initialize,
    setNotifications,
    addNotification,
    updateNotification,
    updateNotificationProgress,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    removeNotification,
    updateUnreadCount,
    setLoading,
    setError,
    setFilters,
    clearFilters,
    setPagination,
    setRealTimeEnabled,
    setLastSync,
    clear,

    // Real-time handlers
    handleEchoNotificationEvent,
    handleEchoProgressUpdate,
    handleEchoGroupAction,
  }
})
